// File: frontend/src/services/reputationService.js
// Version: 1.10
// Date: 2025-12-18 14:50 EST
// Author: Alexander Fitenko (elfny.com) for NYgot LLC (Nygot.com)
// Description: API service for AI Reputation Module (Includes AI Rewrite, Battle & History Fix)
// History:
// - [v1.10]: Standardized API URL to use relative path (proxy) for CORS fix

import axios from 'axios';

// ==========================================
// 1. CONFIGURATION
// ==========================================

const API_TIMEOUT = 300000; // 300 seconds (5 minutes)
const DEFAULT_LLM_MODEL = 'gpt-4o';

// Use relative path to leverage Nginx proxy (avoiding CORS)
// Matches apiService.js logic
const API_URL = '/api/v1';

// ==========================================
// 2. AXIOS INSTANCE & INTERCEPTORS
// ==========================================

const apiClient = (typeof axios.create === 'function')
  ? axios.create({
      baseURL: API_URL,
      timeout: API_TIMEOUT,
      withCredentials: true, // Enables sending secure HttpOnly cookies across origins if needed
      headers: {
        'Content-Type': 'application/json',
      }
    })
  : (axios.default || axios);

/**
 * Response Interceptor
 * Centralized error handling for the entire application.
 */
if (apiClient && apiClient.interceptors && apiClient.interceptors.response && apiClient.interceptors.response.use) {
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // 1. Handle Auth Errors (401)
    if (error.response?.status === 401) {
      console.error('[ReputationService] Unauthorized - redirecting to login');
      // Prevent redirect loop if already on login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(new Error('Session expired. Please login again.'));
    }

    // 2. Handle Permission Errors (403)
    if (error.response?.status === 403) {
      return Promise.reject(new Error('Access denied. You do not have permission.'));
    }

    // 3. Handle Network/Timeout
    if (error.code === 'ECONNABORTED' || !error.response) {
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }

    // 4. Extract Backend Error Message
    const errorMessage = error.response?.data?.message
      || error.response?.data?.detail
      || error.response?.data?.error
      || error.message
      || 'An unexpected error occurred';

    return Promise.reject(new Error(errorMessage));
  }
);
}

// ==========================================
// 3. HELPER FUNCTIONS
// ==========================================

const validateRequired = (data, requiredFields) => {
  const missing = requiredFields.filter(field => !data[field]);
  if (missing.length > 0) {
    throw new Error(`Missing required fields: ${missing.join(', ')}`);
  }
};

// ==========================================
// 4. SERVICE METHODS
// ==========================================

export const reputationService = {

  // --- DASHBOARD & ANALYTICS ---

  getDashboardData: async () => {
    const response = await apiClient.get('/reputation/dashboard');
    return response.data;
  },

  // --- PROFILE MANAGEMENT ---

  getProfiles: async () => {
    const response = await apiClient.get('/reputation/profiles');
    return response.data;
  },

  createProfile: async (data) => {
    validateRequired(data, ['company_name', 'company_url']);
    const response = await apiClient.post('/reputation/profiles', data);
    return response.data;
  },

  getProfile: async (profileId) => {
    if (!profileId) throw new Error('Profile ID is required');
    // Cache buster included to ensure freshness on re-open
    const response = await apiClient.get(`/reputation/profiles/${profileId}?_t=${new Date().getTime()}`);
    return response.data;
  },

  updateProfile: async (profileId, data) => {
    if (!profileId) throw new Error('Profile ID is required');
    const response = await apiClient.put(`/reputation/profiles/${profileId}`, data);
    return response.data;
  },

  deleteProfile: async (profileId) => {
    if (!profileId) throw new Error('Profile ID is required');
    const response = await apiClient.delete(`/reputation/profiles/${profileId}`);
    return response.data;
  },

  /**
   * Set Main Location (Updates is_main=true, backend handles unique constraint)
   */
  setMainLocation: async (profileId) => {
    if (!profileId) throw new Error('Profile ID is required');
    // We only need to send is_main: true. Backend unsets others.
    return reputationService.updateProfile(profileId, { is_main: true });
  },

  smartScan: async (url) => {
    if (!url) throw new Error('URL is required');
    const response = await apiClient.post('/reputation/profiles/smart-scan', { url });
    return response.data;
  },

  /**
   * Upload Media (Images/Logos)
   * Explicitly sets Content-Type header for file uploads
   */
  uploadMedia: async (file) => {
    if (!file) throw new Error('No file provided');

    const formData = new FormData();
    formData.append('file', file);

    // Axios requires specific config to handle FormData correctly (override default JSON)
    const response = await apiClient.post('/media/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // --- AUDIT OPERATIONS ---

  /**
   * Run a full audit (Generic)
   */
  runAudit: async (data) => {
    validateRequired(data, ['company_name', 'company_url']);

    const auditData = {
      llm_model: DEFAULT_LLM_MODEL,
      audit_type: 'full',
      ...data
    };

    const response = await apiClient.post('/reputation/audit/run', auditData);

    if (!response.data.task_id) {
      throw new Error('Server accepted request but did not return a Task ID.');
    }

    return response.data;
  },

  /**
   * Run Audit for existing Profile
   */
  runAuditForProfile: async (profileId, model = DEFAULT_LLM_MODEL) => {
    if (!profileId) throw new Error('Profile ID is required');

    const response = await apiClient.post(
      `/reputation/audit/run/${profileId}`,
      { llm_model: model }
    );

    return response.data;
  },

  /**
   * [FIX V5.1] runAuditTask
   * Alias for runAuditForProfile to match Dashboard component calls.
   * Handles the { profileId, payload } structure often used in generic handlers.
   */
  runAuditTask: async (profileId, payload) => {
    // Extract model from payload if present, otherwise default
    const model = payload?.llm_model || DEFAULT_LLM_MODEL;
    return reputationService.runAuditForProfile(profileId, model);
  },

  getTaskStatus: async (taskId) => {
    if (!taskId) throw new Error('Task ID is required');
    const response = await apiClient.get(`/reputation/audit/task/${taskId}`);
    return response.data;
  },

  getAuditDetails: async (auditId) => {
    if (!auditId) throw new Error('Audit ID is required');
    const response = await apiClient.get(`/reputation/audit/${auditId}`);
    return response.data;
  },

  getAuditHistory: async (profileId) => {
    if (!profileId) throw new Error('Profile ID is required');
    const response = await apiClient.get(`/reputation/audit/history/${profileId}`);
    return response.data;
  },

  // --- AGENT BATTLES (CHAT) ---

  /**
   * Get Battle Sessions
   * Accepts optional profileId and location to filter battles.
   */
  getBattleSessions: async (profileId = null, location = null) => {
    let url = '/reputation/battle/sessions';
    const params = new URLSearchParams();

    if (profileId) params.append('profile_id', profileId);
    if (location) params.append('location_query', location);

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * [FIX V5.1] getBattles
   * Alias for getBattleSessions to match Dashboard component calls.
   */
  getBattles: async () => {
    return reputationService.getBattleSessions();
  },

  /**
   * Start a Battle (Single or Swarm)
   * @param {Object} data - { profile_id, keyword, worker_count, worker_keys }
   */
  startBattle: async (data) => {
    validateRequired(data, ['profile_id', 'keyword']);
    // 'worker_count' is optional (defaults to 1 in backend if missing)
    const response = await apiClient.post('/reputation/battle/start', data);
    return response.data;
  },

  /**
   * [FIX V5.1] runBattle
   * Alias for startBattle to match Dashboard button handler.
   */
  runBattle: async (data) => {
    return reputationService.startBattle(data);
  },

  sendBattleReply: async (sessionId, message) => {
    if (!sessionId) throw new Error('Session ID is required');
    if (!message || !message.trim()) throw new Error('Message cannot be empty');

    // [FIX] Backend expects 'content' field in Pydantic schema, not 'message'
    const response = await apiClient.post(`/reputation/battle/${sessionId}/reply`, { content: message });
    return response.data;
  },

  // --- CONTENT WARFARE (GENERATORS) ---

  /**
   * 1. Get List of Generated Fixes (Schema + Articles)
   */
  listProfileFixes: async (profileId) => {
    if (!profileId) throw new Error('Profile ID is required');

    const response = await apiClient.get(`/reputation/profiles/${profileId}/fixes`);
    return response.data;
  },

  // Alias for backward compatibility
  getFixes: async (profileId) => {
    return reputationService.listProfileFixes(profileId);
  },

  /**
   * 2. Generate Technical Fixes (Schema)
   */
  generateFixes: async (profileId, fixTypes = ["json_ld"]) => {
    if (!profileId) throw new Error('Profile ID is required');

    const response = await apiClient.post('/reputation/fixes', {
      profile_id: profileId,
      fix_types: fixTypes
    });
    return response.data;
  },

  /**
   * 3. Generate Semantic Content (Articles/Snippets)
   */
  generateContentFix: async (profileId, refusalReason, contentType = 'snippet') => {
    if (!profileId) throw new Error('Profile ID is required');

    const response = await apiClient.post('/reputation/warfare/generate-fix', {
      profile_id: profileId,
      refusal_reason: refusalReason,
      content_type: contentType
    });
    return response.data;
  },

  /**
   * AI Rewrite / Optimize Content
   * Uses AI to improve text based on context.
   */
  aiRewrite: async (data) => {
    // data example: { text: "...", type: "keywords", context: "Company Name, Bronx..." }
    const response = await apiClient.post('/reputation/ai-rewrite', data);
    return response.data;
  },

  /**
   * Post generated Fix (Article) to WordPress
   */
  postToWordpress: async (fixId) => {
    if (!fixId) throw new Error('Fix ID is required');
    const response = await apiClient.post(`/reputation/warfare/${fixId}/post-to-wp`);
    return response.data;
  },

  /**
   * Operation Phoenix: Trigger Batch Remaster
   */
  triggerPhoenixRemaster: async (year, month = null, limit = 5, delayMinutes = 0) => {
    const payload = { year, limit, delay_minutes: delayMinutes };
    if (month) payload.month = month;

    // Note: warfare router is mounted directly under /reputation, not /reputation/warfare
    const response = await apiClient.post('/reputation/phoenix/remaster', payload);
    return response.data;
  },

  // --- GOOGLE BUSINESS PROFILE (GMB) INTEGRATION ---
  getGmbAuthStatus: async () => {
    const response = await apiClient.get('/reputation/gmb/auth/status');
    return response.data;
  },

  getGmbLocation: async (profileId) => {
    if (!profileId) throw new Error('Profile ID is required');
    const response = await apiClient.get(`/reputation/gmb/locations/${profileId}`);
    return response.data;
  },

  syncGmbLocation: async (profileId) => {
    if (!profileId) throw new Error('Profile ID is required');
    const response = await apiClient.post(`/reputation/gmb/locations/${profileId}/sync`);
    return response.data;
  },

  draftGmbReply: async (profileId, reviewId) => {
    const response = await apiClient.post(`/reputation/gmb/locations/${profileId}/reviews/${reviewId}/draft-reply`);
    return response.data;
  },

  sendGmbReply: async (profileId, reviewId, replyText) => {
    const response = await apiClient.post(`/reputation/gmb/locations/${profileId}/reviews/${reviewId}/reply`, { reply_text: replyText });
    return response.data;
  },

  approveGmbPost: async (profileId, postId) => {
    if (!profileId) throw new Error('profileId is required');
    if (!postId) throw new Error('postId is required');
    const response = await apiClient.post(`/reputation/gmb/locations/${profileId}/posts/${postId}/approve`);
    return response.data;
  },

  markPostPublishedManually: async (profileId, postId, googlePostUrl = null) => {
    if (!profileId) throw new Error('profileId is required');
    if (!postId) throw new Error('postId is required');
    const response = await apiClient.post(
      `/reputation/gmb/locations/${profileId}/posts/${postId}/mark-published-manually`,
      { google_post_url: googlePostUrl || null }
    );
    return response.data;
  },

  rejectGmbPost: async (profileId, postId, reason) => {
    if (!profileId) throw new Error('profileId is required');
    if (!postId) throw new Error('postId is required');
    const response = await apiClient.post(`/reputation/gmb/locations/${profileId}/posts/${postId}/reject`, { reason });
    return response.data;
  },

  rewriteGmbPost: async (profileId, postId, feedback) => {
    if (!profileId) throw new Error('profileId is required');
    if (!postId) throw new Error('postId is required');
    const response = await apiClient.post(`/reputation/gmb/locations/${profileId}/posts/${postId}/rewrite`, { feedback });
    return response.data;
  },

  deleteGmbPost: async (profileId, postId) => {
    if (!profileId) throw new Error('profileId is required');
    if (!postId) throw new Error('postId is required');
    const response = await apiClient.delete(`/reputation/gmb/locations/${profileId}/posts/${postId}`);
    return response.data;
  },

  // --- GEOGRID VISIBILITY INTEGRATION (C2) ---
  getGeogridByKluch: async (kluch) => {
    if (!kluch) throw new Error('Office kluch is required');
    const response = await apiClient.get(`/reputation/geogrid/${kluch}`);
    return response.data;
  },

  getGeogridSettings: async (kluch) => {
    if (!kluch) throw new Error('Office kluch is required');
    const response = await apiClient.get(`/reputation/geogrid/settings/${kluch}`);
    return response.data;
  },

  saveGeogridSettings: async (kluch, keywords) => {
    if (!kluch) throw new Error('Office kluch is required');
    const response = await apiClient.put(`/reputation/geogrid/settings/${kluch}`, { keywords });
    return response.data;
  }

};

// Export raw client for custom edge cases if needed
export { apiClient };

// Default export
export default reputationService;